// apps/web/src/layouts/components/useWebPushDevice.ts
//
// Orquestra a inscrição Web Push deste navegador/dispositivo: permissão,
// service worker, PushManager subscribe/unsubscribe + POST/DELETE no backend.
// Contrato: docs/contracts/ESCR-web-push-transport.md.
//
// NO-OP seguro quando o navegador não suporta (sem serviceWorker/PushManager)
// ou quando VAPID está desabilitado no backend (publicKey === '').

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  urlBase64ToUint8Array,
  useSubscribeWebPush,
  useUnsubscribeWebPush,
  useVapidPublicKey,
} from '@alamo/api';

export type WebPushStatus =
  | 'unsupported' // navegador sem SW/PushManager OU VAPID off no backend
  | 'denied' // permissão bloqueada pelo usuário
  | 'subscribed' // inscrito neste dispositivo
  | 'idle'; // suportado, ainda não inscrito (default/prompt-able)

export type WebPushActionFailure =
  | 'unsupported'
  | 'denied'
  | 'permission_error'
  | 'service_worker_error'
  | 'subscription_error'
  | 'subscription_timeout'
  | 'invalid_subscription'
  | 'registration_error'
  | 'disable_error';
export type WebPushActionResult = 'success' | WebPushActionFailure;
export type WebPushOperation = 'checking' | 'enabling' | 'disabling' | null;

export const WEB_PUSH_OPERATION_TIMEOUT_MS = 10_000;
export const WEB_PUSH_SUBSCRIPTION_TIMEOUT_MS = 30_000;

class WebPushTimeoutError extends Error {
  constructor() {
    super('WEB_PUSH_OPERATION_TIMEOUT');
    this.name = 'WebPushTimeoutError';
  }
}

export interface UseWebPushDeviceResult {
  status: WebPushStatus;
  /** Qualquer etapa em andamento, incluindo permissão e service worker. */
  busy: boolean;
  operation: WebPushOperation;
  /** true quando o transporte está disponível (suportado + VAPID on). */
  available: boolean;
  /** Liga as notificações neste dispositivo (pede permissão + inscreve). */
  enable: () => Promise<WebPushActionResult>;
  /** Desliga as notificações neste dispositivo (DELETE + unsubscribe). */
  disable: () => Promise<WebPushActionResult>;
  /** Última falha classificada para feedback acionável na UI. */
  lastError: WebPushActionFailure | null;
}

function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs = WEB_PUSH_OPERATION_TIMEOUT_MS,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new WebPushTimeoutError());
    }, timeoutMs);

    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

function isTimeoutError(error: unknown): boolean {
  return error instanceof WebPushTimeoutError;
}

async function getPushServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  const container = navigator.serviceWorker;

  if (typeof container.getRegistration !== 'function') {
    return withTimeout(container.ready);
  }

  const existing = await withTimeout(container.getRegistration());
  if (existing?.active) return existing;

  const registration = existing ?? (await withTimeout(container.register('/sw.js')));
  if (registration.active) return registration;

  return withTimeout(container.ready);
}

/**
 * Safari 18.4+ exposes Declarative Web Push through `window.pushManager`.
 * When a root service worker also exists, WebKit shares the same subscription
 * with `ServiceWorkerRegistration.pushManager`. Prefer the window-level API on
 * Apple Home Screen apps so a delayed service-worker lookup cannot block the
 * device toggle.
 */
function getWindowPushManager(): PushManager | null {
  if (typeof window === 'undefined') return null;
  const manager = (window as Window & { pushManager?: PushManager }).pushManager;
  if (
    !manager ||
    typeof manager.getSubscription !== 'function' ||
    typeof manager.subscribe !== 'function'
  ) {
    return null;
  }
  return manager;
}

async function getPushManager(): Promise<PushManager> {
  const windowManager = getWindowPushManager();
  if (windowManager) return windowManager;

  // Chromium/Android and browsers implementing the standard Push API expose
  // PushManager on the active ServiceWorkerRegistration.
  const registration = await getPushServiceWorkerRegistration();
  return registration.pushManager;
}

/**
 * Best-effort lookup used only to paint the current state. It must not install
 * or wait for a service worker: enabling remains a direct user action, which is
 * required by WebKit for notification permission.
 */
async function getExistingPushSubscription(): Promise<PushSubscription | null> {
  const windowManager = getWindowPushManager();
  if (windowManager) return withTimeout(windowManager.getSubscription());

  const container = navigator.serviceWorker;
  if (typeof container.getRegistration !== 'function') return null;
  const registration = await withTimeout(container.getRegistration());
  if (!registration?.active) return null;
  return withTimeout(registration.pushManager.getSubscription());
}

function isPushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function useWebPushDevice(): UseWebPushDeviceResult {
  const supported = isPushSupported();

  const { data: publicKey } = useVapidPublicKey({ enabled: supported });
  const subscribeMut = useSubscribeWebPush();
  const unsubscribeMut = useUnsubscribeWebPush();

  // VAPID off no backend (publicKey === '') ⇒ transporte indisponível.
  const available = supported && typeof publicKey === 'string' && publicKey.length > 0;

  const [status, setStatus] = useState<WebPushStatus>(supported ? 'idle' : 'unsupported');
  const [lastError, setLastError] = useState<WebPushActionFailure | null>(null);
  const [operation, setOperation] = useState<WebPushOperation>(null);
  const actionVersionRef = useRef(0);

  // Sincroniza o status inicial com a permissão + inscrição existente.
  useEffect(() => {
    if (!available) {
      setStatus('unsupported');
      setOperation(null);
      return;
    }
    if (Notification.permission === 'denied') {
      setStatus('denied');
      setOperation(null);
      return;
    }
    let cancelled = false;
    const actionVersion = actionVersionRef.current;
    getExistingPushSubscription()
      .then((sub) => {
        if (cancelled || actionVersionRef.current !== actionVersion) return;
        setStatus(sub ? 'subscribed' : 'idle');
      })
      .catch(() => {
        if (!cancelled && actionVersionRef.current === actionVersion) setStatus('idle');
      });
    return () => {
      cancelled = true;
    };
  }, [available]);

  const enable = useCallback(async (): Promise<WebPushActionResult> => {
    if (!available || typeof publicKey !== 'string' || publicKey.length === 0) {
      return 'unsupported';
    }
    setLastError(null);
    actionVersionRef.current += 1;
    setOperation('enabling');
    const fail = (failure: WebPushActionFailure): WebPushActionFailure => {
      console.error(`[web-push] enable failed: ${failure}`);
      setLastError(failure);
      return failure;
    };
    try {
      let permission: NotificationPermission;
      try {
        // A decisão fica nas mãos do usuário; não expire o prompt enquanto ele
        // ainda está escolhendo nas configurações nativas do navegador.
        permission = await Notification.requestPermission();
      } catch {
        return fail('permission_error');
      }
      if (permission !== 'granted') {
        setStatus('denied');
        setLastError('denied');
        return 'denied';
      }

      let pushManager: PushManager;
      try {
        pushManager = await getPushManager();
      } catch {
        return fail('service_worker_error');
      }

      let subscription: PushSubscription;
      try {
        const existing = await withTimeout(pushManager.getSubscription());
        subscription =
          existing ??
          (await withTimeout(
            pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: urlBase64ToUint8Array(publicKey),
            }),
            WEB_PUSH_SUBSCRIPTION_TIMEOUT_MS,
          ));
      } catch (error: unknown) {
        return fail(isTimeoutError(error) ? 'subscription_timeout' : 'subscription_error');
      }

      let subscriptionJson: PushSubscriptionJSON;
      try {
        subscriptionJson = subscription.toJSON();
      } catch {
        return fail('invalid_subscription');
      }
      if (
        !subscriptionJson.endpoint ||
        !subscriptionJson.keys?.p256dh ||
        !subscriptionJson.keys?.auth
      ) {
        return fail('invalid_subscription');
      }

      try {
        await withTimeout(
          subscribeMut.mutateAsync({
            endpoint: subscriptionJson.endpoint,
            keys: {
              p256dh: subscriptionJson.keys.p256dh,
              auth: subscriptionJson.keys.auth,
            },
          }),
        );
      } catch {
        return fail('registration_error');
      }
      setStatus('subscribed');
      return 'success';
    } finally {
      setOperation(null);
    }
  }, [available, publicKey, subscribeMut]);

  const disable = useCallback(async (): Promise<WebPushActionResult> => {
    if (!supported) return 'unsupported';
    setLastError(null);
    actionVersionRef.current += 1;
    setOperation('disabling');
    const fail = (): WebPushActionFailure => {
      console.error('[web-push] disable failed: disable_error');
      setLastError('disable_error');
      return 'disable_error';
    };
    try {
      let pushManager: PushManager;
      try {
        pushManager = await getPushManager();
      } catch {
        return fail();
      }
      let subscription: PushSubscription | null;
      try {
        subscription = await withTimeout(pushManager.getSubscription());
      } catch {
        return fail();
      }
      if (subscription) {
        try {
          await withTimeout(
            Promise.all([
              unsubscribeMut.mutateAsync({ endpoint: subscription.endpoint }),
              subscription.unsubscribe(),
            ]),
          );
        } catch {
          return fail();
        }
      }
      setStatus('idle');
      return 'success';
    } finally {
      setOperation(null);
    }
  }, [supported, unsubscribeMut]);

  return {
    status,
    busy: operation !== null || subscribeMut.isPending || unsubscribeMut.isPending,
    operation,
    available,
    enable,
    disable,
    lastError,
  };
}
